import React from 'react'

import { PartnerInvestor } from '../../services/partner.types'
import {
  formatPartnerCents,
  formatPartnerDate,
  formatPartnerMoney,
  formatPartnerQuantity,
  formatPartnerStatus,
} from './partner-format'
import { PartnerStatus } from './partner-shell'

const display = (value?: string | number | null) => {
  if (value === undefined || value === null || value === '') return '—'
  return String(value)
}

const PartnerMoneyCell = ({ cents }: { cents?: number | null }) => (
  <td className='partner-table__amount'>{formatPartnerCents(cents)}</td>
)

const rate = (value?: number | null) => (value === undefined || value === null ? '—' : `${value}%`)

export const PartnerInvestorDetail = ({
  investor,
  onBack,
}: {
  investor: PartnerInvestor
  onBack: () => void
}) => {
  const wallet = investor.wallet
  const movements = wallet?.movements || []
  const interestRows = [
    ...investor.interests.map((row) => ({ ...row, source: 'interest' })),
    ...investor.payouts.map((row) => ({ ...row, source: 'payout' })),
  ].sort((a, b) => {
    const aTime = a.date ? Date.parse(a.date) : 0
    const bTime = b.date ? Date.parse(b.date) : 0
    return bTime - aTime
  })

  return (
    <div data-testid='partner-investor-detail'>
      <button type='button' className='partner-back' onClick={onBack}>
        Investor book
      </button>
      <div className='partner-page-heading'>
        <div>
          <h1>{investor.name || 'Investor'}</h1>
          <p>{investor.investorReference || '—'}</p>
        </div>
        <PartnerStatus status={investor.accountStatus} />
      </div>

      <section className='partner-card' aria-label='Investor profile'>
        <h2>Profile</h2>
        <dl className='partner-facts partner-facts--wide'>
          <div>
            <dt>Client ID</dt>
            <dd>{display(investor.investorReference)}</dd>
          </div>
          <div>
            <dt>Account</dt>
            <dd>
              <PartnerStatus status={investor.accountStatus} />
            </dd>
          </div>
        </dl>
      </section>

      <section className='partner-card' aria-label='KYC'>
        <h2>KYC</h2>
        <dl className='partner-facts'>
          <div>
            <dt>Status</dt>
            <dd>
              <PartnerStatus status={investor.kyc?.status} />
            </dd>
          </div>
          <div>
            <dt>Approved date</dt>
            <dd>{formatPartnerDate(investor.kyc?.approvalDate)}</dd>
          </div>
        </dl>
      </section>

      <section className='partner-card' aria-label='Investments'>
        <h2>Investments</h2>
        {investor.investments.length === 0 ? (
          <p className='partner-empty'>No investments yet.</p>
        ) : (
          <div className='partner-table-scroll'>
            <table className='partner-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Opportunity</th>
                  <th>Asset class</th>
                  <th>Amount</th>
                  <th>Quantity</th>
                  <th>Order</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {investor.investments.map((row) => (
                  <tr key={row.id}>
                    <td>{formatPartnerDate(row.createdAt)}</td>
                    <td>{display(row.opportunityTitle)}</td>
                    <td>{display(row.assetClass)}</td>
                    <td className='partner-table__amount'>
                      {formatPartnerMoney(row.amount, row.currencyCode)}
                    </td>
                    <td>{formatPartnerQuantity(row.remainingQuantity)}</td>
                    <td>{display(row.orderId)}</td>
                    <td>
                      <PartnerStatus status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className='partner-card' aria-label='Redemptions'>
        <h2>Redemptions</h2>
        <p className='partner-section-note'>
          Opportunity withdrawals, not wallet cash withdrawals.
        </p>
        {investor.redemptions.length === 0 ? (
          <p className='partner-empty'>No redemptions yet.</p>
        ) : (
          <div className='partner-table-scroll'>
            <table className='partner-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Opportunity</th>
                  <th>Amount</th>
                  <th>Received</th>
                  <th>Sent</th>
                  <th>Quantity</th>
                  <th>Order</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {investor.redemptions.map((row) => (
                  <tr key={row.id}>
                    <td>{formatPartnerDate(row.createdAt)}</td>
                    <td>{display(row.opportunityTitle)}</td>
                    <td className='partner-table__amount'>{formatPartnerMoney(row.amount)}</td>
                    <td>{formatPartnerMoney(row.actualAmountReceived)}</td>
                    <td>{formatPartnerMoney(row.actualAmountSent)}</td>
                    <td>{formatPartnerQuantity(row.quantity)}</td>
                    <td>{display(row.orderId)}</td>
                    <td>
                      <PartnerStatus status={row.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className='partner-card' aria-label='Interest/Dividend'>
        <h2>Interest/Dividend</h2>
        {interestRows.length === 0 ? (
          <p className='partner-empty'>No interest or dividends yet.</p>
        ) : (
          <div className='partner-table-scroll'>
            <table className='partner-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Opportunity</th>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Quantity</th>
                </tr>
              </thead>
              <tbody>
                {interestRows.map((row) => (
                  <tr key={`${row.source}-${row.id}`}>
                    <td>{formatPartnerDate(row.date)}</td>
                    <td>{display(row.opportunityTitle)}</td>
                    <td>{formatPartnerStatus(row.txnType)}</td>
                    <td className='partner-table__amount'>{formatPartnerMoney(row.amount)}</td>
                    <td>{formatPartnerQuantity(row.quantity)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className='partner-card' aria-label='Interest terms'>
        <h2>Interest terms</h2>
        {investor.interestTerms.length === 0 ? (
          <p className='partner-empty'>No interest terms yet.</p>
        ) : (
          <div className='partner-table-scroll'>
            <table className='partner-table'>
              <thead>
                <tr>
                  <th>Opportunity</th>
                  <th>Rate</th>
                  <th>Frequency</th>
                </tr>
              </thead>
              <tbody>
                {investor.interestTerms.map((row) => (
                  <tr key={row.id}>
                    <td>{display(row.opportunityTitle)}</td>
                    <td>{rate(row.interestRate)}</td>
                    <td>{display(row.payoutFrequency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className='partner-card' aria-label='Wallet'>
        <h2>Wallet</h2>
        <dl className='partner-facts partner-facts--wide partner-wallet-summary'>
          <div>
            <dt>Available</dt>
            <dd>{formatPartnerCents(wallet?.availableCents)}</dd>
          </div>
          <div>
            <dt>Pending top up</dt>
            <dd>{formatPartnerCents(wallet?.pendingTopupCents)}</dd>
          </div>
          <div>
            <dt>Pending withdrawal</dt>
            <dd>{formatPartnerCents(wallet?.pendingWithdrawCents)}</dd>
          </div>
        </dl>
        {movements.length === 0 ? (
          <p className='partner-empty'>No wallet movements yet.</p>
        ) : (
          <div className='partner-table-scroll'>
            <table className='partner-table'>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Type</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {movements.map((row) => (
                  <tr key={row.id}>
                    <td>{formatPartnerDate(row.createdAt)}</td>
                    <td>{formatPartnerStatus(row.type)}</td>
                    <PartnerMoneyCell cents={row.amountCents} />
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  )
}
